import { ButtonContent, PendingOverlay } from "@ui/components/internal/ButtonContent";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button, type ButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";

const retroButton = tv({
  base: [
    "relative inline-flex cursor-pointer items-center justify-center rounded-full px-5 font-semibold text-green-950 transition duration-100 ease-linear",
    "shadow-[inset_0_0_0_1px_var(--color-green-950),-4px_4px_0_0_var(--color-green-950)]",
    "hover:shadow-[inset_0_0_0_1px_var(--color-green-950),-2px_2px_0_0_var(--color-green-950)]",
    "pressed:shadow-[inset_0_0_0_1px_var(--color-green-950),0_0_0_0_var(--color-green-950)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "data-[pending]:cursor-default",
  ],
  defaultVariants: {
    color: "yellow",
    size: "48",
  },
  variants: {
    color: {
      green: "bg-green-50",
      yellow: "bg-yellow-500",
    },
    size: {
      "40": "py-2.5 text-base leading-5",
      "48": "py-3 text-xl leading-6",
    },
  },
});

type RetroButtonVariants = VariantProps<typeof retroButton>;

export type RetroButtonProps = ButtonProps & {
  color?: RetroButtonVariants["color"];
  size?: RetroButtonVariants["size"];
};

export function RetroButton({ color, size, ...props }: RetroButtonProps) {
  return (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        retroButton({ ...renderProps, color, size, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isPending }) => (
        <>
          <ButtonContent isPending={isPending}>{children}</ButtonContent>
          <PendingOverlay isPending={isPending} />
        </>
      ))}
    </Button>
  );
}
